import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { message } from 'antd';
import { postAuthenticatedRequest } from '../../../utils/api/requests/request';

const WorkflowContext = createContext();

const STATE_CACHE_TTL = 30000;

export const WorkflowProvider = ({ children }) => {
    // Simple state management
    const [processInstances, setProcessInstances] = useState({});
    const [instanceStates, setInstanceStates] = useState({});

    // Cache reads go through refs to keep the fetchers referentially stable.
    // Depending on the state objects re-fires consumers' effects on every write.
    const processInstancesRef = useRef({});
    const instanceStatesRef = useRef({});

    // Use refs for data that shouldn't trigger re-renders
    const lastRequestsRef = useRef({
        instances: null,
        instanceState: null
    });

    // Track pending requests to prevent duplicates. `forced` keeps a refresh
    // from being served an in-flight cached request.
    const pendingRequestsRef = useRef(new Map());

    // Loading states
    const [loading, setLoading] = useState({
        instances: false,
        instanceState: false,
        definition: false
    });

    const takePendingRequest = (key, forceRefresh) => {
        const pending = pendingRequestsRef.current.get(key);
        if (!pending) return null;
        if (forceRefresh && !pending.forced) return null;
        return pending.promise;
    };

    // ==================== API FUNCTIONS ====================

    // Get process instances directly
    const getProcessInstances = useCallback(async (entityId, entityReferenceId, page = 1, pageSize = 10, forceRefresh = false, shouldShowClosedProcess = false) => {
        // Create unique key for this request
        const requestKey = `${entityReferenceId}-${entityId}-${page}-${pageSize}`;
        const pendingKey = `instances:${requestKey}`;

        const pending = takePendingRequest(pendingKey, forceRefresh);
        if (pending) return pending;

        // Check if this is the same as last request and not forcing refresh
        if (!forceRefresh && lastRequestsRef.current.instances === requestKey) {
            // Return existing data if available
            const existingData = processInstancesRef.current[requestKey];
            if (existingData) {
                return existingData;
            }
        }

        try {
            setLoading(prev => ({ ...prev, instances: true }));

            // Create a promise for this request
            const requestPromise = (async () => {
                // Update last request
                lastRequestsRef.current.instances = requestKey;

                const data = {
                    entityReferenceId: entityReferenceId,
                    entityType: entityId,
                    pageSize,
                    shouldShowClosedProcess,
                    pageIndex: page - 1,
                };

                const response = await postAuthenticatedRequest(
                    "GetProcessInstanceListQuery",
                    data
                );

                if (response?.ok && response?.outData?.isSuccessful) {
                    // Extract instances from response.outData.data.instances
                    const responseData = response.outData.data?.instances || [];
                    const totalRows = response.outData.data?.count || response.outData.totalRows || 0;

                    const result = {
                        instances: responseData,
                        totalRows,
                        page,
                        pageSize,
                        _timestamp: Date.now(),
                        requestKey,
                        responseData: response.outData.data // Store full response data if needed
                    };

                    processInstancesRef.current = {
                        ...processInstancesRef.current,
                        [requestKey]: result
                    };
                    setProcessInstances(processInstancesRef.current);

                    return result;
                } else {
                    throw new Error(response?.outData?.message ?? "Failed to fetch process instances");
                }
            })();

            // Store the promise
            pendingRequestsRef.current.set(pendingKey, { promise: requestPromise, forced: forceRefresh });

            const result = await requestPromise;
            return result;
        } catch (error) {
            console.error('Error fetching process instances:', error);
            message.error(error.message);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, instances: false }));
            // Remove from pending requests
            pendingRequestsRef.current.delete(pendingKey);
        }
    }, []);

    // Get process instance state directly
    const getProcessInstanceState = useCallback(async (instanceGuid, forceRefresh = false) => {
        const pendingKey = `state:${instanceGuid}`;

        const pending = takePendingRequest(pendingKey, forceRefresh);
        if (pending) return pending;

        // Check cache (with timestamp for freshness)
        const cachedData = instanceStatesRef.current[instanceGuid];
        const cacheTimestamp = cachedData?._timestamp;
        const now = Date.now();

        if (!forceRefresh && cachedData && cacheTimestamp && (now - cacheTimestamp) < STATE_CACHE_TTL) {
            return cachedData;
        }

        try {
            setLoading(prev => ({ ...prev, instanceState: true }));

            // Create a promise for this request
            const requestPromise = (async () => {
                // Update last request
                lastRequestsRef.current.instanceState = instanceGuid;

                const data = { instanceGuid };
                const response = await postAuthenticatedRequest(
                    "GetProcessInstanceStateCommand",
                    data
                );

                if (response?.ok && response?.outData?.isSuccessful) {
                    // The instance state is in response.outData.data
                    const instanceData = response.outData.data;

                    const result = {
                        ...instanceData,
                        _timestamp: Date.now(), // Add timestamp for caching
                        instanceGuid // Store the instanceGuid in the data
                    };

                    instanceStatesRef.current = {
                        ...instanceStatesRef.current,
                        [instanceGuid]: result
                    };
                    setInstanceStates(instanceStatesRef.current);

                    return result;
                } else {
                    throw new Error(response?.outData?.message ?? "Failed to fetch instance state");
                }
            })();

            // Store the promise
            pendingRequestsRef.current.set(pendingKey, { promise: requestPromise, forced: forceRefresh });

            const result = await requestPromise;
            return result;
        } catch (error) {
            console.error('Error fetching instance state:', error);
            message.error(error.message);
            throw error;
        } finally {
            setLoading(prev => ({ ...prev, instanceState: false }));
            // Remove from pending requests
            pendingRequestsRef.current.delete(pendingKey);
        }
    }, []);

    // Get workflow details for entity
    const getWorkflowDetailsForEntity = useCallback(async (entityId, entityReferenceId, forceRefresh = false) => {
        try {
            // First get instances
            const instances = await getProcessInstances(entityId, entityReferenceId, 1, 5, forceRefresh);

            if (!instances?.instances?.length) {
                return null;
            }

            // Get details for the first instance
            const firstInstance = instances.instances[0];
            if (firstInstance?.instanceGuid) {
                const instanceState = await getProcessInstanceState(firstInstance.instanceGuid, forceRefresh);
                return {
                    ...instanceState,
                    instanceGuid: firstInstance.instanceGuid,
                    processInstanceId: firstInstance.processInstanceId
                };
            }

            return null;
        } catch (error) {
            console.error('Error getting workflow details for entity:', error);
            return null;
        }
    }, [getProcessInstances, getProcessInstanceState]);

    // Clear specific data
    const clearData = useCallback((dataType, key = null) => {
        switch (dataType) {
            case 'instances':
                if (key) {
                    const next = { ...processInstancesRef.current };
                    delete next[key];
                    processInstancesRef.current = next;
                } else {
                    processInstancesRef.current = {};
                }
                setProcessInstances(processInstancesRef.current);
                break;
            case 'states':
                if (key) {
                    const next = { ...instanceStatesRef.current };
                    delete next[key];
                    instanceStatesRef.current = next;
                } else {
                    instanceStatesRef.current = {};
                }
                setInstanceStates(instanceStatesRef.current);
                break;
            case 'lastRequests':
                lastRequestsRef.current = {
                    instances: null,
                    instanceState: null
                };
                break;
            case 'all':
                processInstancesRef.current = {};
                instanceStatesRef.current = {};
                setProcessInstances({});
                setInstanceStates({});
                lastRequestsRef.current = {
                    instances: null,
                    instanceState: null
                };
                pendingRequestsRef.current.clear();
                break;
        }
    }, []);

    // Refresh specific data
    const refreshData = useCallback(async (dataType, identifier) => {
        switch (dataType) {
            case 'instances': {
                const [entityReferenceId, entityId, page, pageSize] = identifier.split('-');
                return await getProcessInstances(entityId, entityReferenceId, parseInt(page), parseInt(pageSize), true);
            }
            case 'state':
                return await getProcessInstanceState(identifier, true);
            case 'entityWorkflow': {
                const [entityIdParam, entityReferenceIdParam] = identifier.split('|');
                return await getWorkflowDetailsForEntity(entityIdParam, entityReferenceIdParam, true);
            }
            default:
                return undefined;
        }
    }, [getProcessInstances, getProcessInstanceState, getWorkflowDetailsForEntity]);

    const value = useMemo(() => ({
        // Data
        loading,
        processInstances,
        instanceStates,

        // Methods
        getProcessInstances,
        getProcessInstanceState,
        getWorkflowDetailsForEntity,

        // Data management
        clearData,
        refreshData,
    }), [
        loading,
        processInstances,
        instanceStates,
        getProcessInstances,
        getProcessInstanceState,
        getWorkflowDetailsForEntity,
        clearData,
        refreshData,
    ]);

    return (
        <WorkflowContext.Provider value={value}>
            {children}
        </WorkflowContext.Provider>
    );
};

export const useWorkflow = () => {
    const context = useContext(WorkflowContext);
    if (!context) {
        throw new Error('useWorkflow must be used within a WorkflowProvider');
    }
    return context;
};
